import { Component, OnInit, Input, ChangeDetectionStrategy, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormBuilder, UntypedFormArray, Validators, ControlValueAccessor } from '@angular/forms';
import { Observable, of, combineLatest } from 'rxjs';
import { map, startWith, shareReplay } from 'rxjs/operators';
import { QuestionAnswer, Question } from '../';
import { FieldEyeComponent } from '../field-eye/field-eye.component';
import { createComponentValueAccessor } from '@app/shared/utils';

interface QuestionsAnswersControlValue {
  questionId: number;
  answer: string;
}

@Component({
  standalone: false,
  selector: 'ti-reset-password-questions-answers',
  templateUrl: './reset-password-questions-answers.component.html',
  styleUrls: ['./reset-password-questions-answers.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [createComponentValueAccessor(ResetPasswordQuestionsAnswersComponent)],
})
export class ResetPasswordQuestionsAnswersComponent implements OnInit, ControlValueAccessor {
  @Input() questionsCount: number;
  @Input() questionsList: Question[];
  @Input() answeredList: QuestionAnswer[];
  @Input() firstSubmit = false;
  @Input() answersMasked = false;
  @Input() confirmUnmask = true;

  strings = tiStrings.auth.registration.step1;

  questionsAnswersForm: UntypedFormArray;

  readonly QUESTION_FIELD = 'questionId';
  readonly ANSWER_FIELD = 'answer';
  readonly miscStrings = tiStrings.app.misc;

  questionsListFiltered$: Observable<Question[]>;
  showProvidePassword = false;
  private toggledEyeIndex: number;

  @ViewChildren(FieldEyeComponent) fieldEyesComponents: QueryList<FieldEyeComponent>;

  private onChange: (value: QuestionAnswer[]) => void;
  private onTouch: (value: QuestionAnswer[]) => void;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit() {
    this.buildForm();
    this.initDropdownsFiltering();
    setTimeout(() => this.setInitialValues()); // setTimeout because registerOnChange don't invoked yet
  }

  private buildForm(): void {
    const configArray = [];

    for (let i = 0; i < this.questionsCount; i++) {
      configArray.push(
        this.fb.group({
          [this.QUESTION_FIELD]: [null, Validators.required],
          [this.ANSWER_FIELD]: [null, Validators.required],
        }),
      );
    }

    this.questionsAnswersForm = this.fb.array(configArray);

    this.questionsAnswersForm.valueChanges.subscribe((values: QuestionsAnswersControlValue[]) => {
      const mapped = this.filterMapToQuestionAnswer(values);
      this.onChange(mapped);
      this.onTouch(mapped);
    });
  }

  private filterMapToQuestionAnswer(values: QuestionsAnswersControlValue[]): QuestionAnswer[] {
    return this.questionsAnswersForm.valid
      ? values.map((item) => {
          const questionItem = this.questionsList.find((el) => el.questionId === item[this.QUESTION_FIELD]);
          return {
            questionId: item[this.QUESTION_FIELD],
            question: questionItem && questionItem.question,
            answer: item[this.ANSWER_FIELD],
          };
        })
      : [];
  }

  private initDropdownsFiltering(): void {
    if (this.questionsCount > 0) {
      const filtersArr$ = this.questionsAnswersForm.controls.map<Observable<number>>((group) => {
        const control = group.get(this.QUESTION_FIELD);
        return control.valueChanges.pipe(startWith(null));
      });

      const filters$ = combineLatest(...filtersArr$);

      this.questionsListFiltered$ = combineLatest(of(this.questionsList), filters$).pipe(
        map(([list, filters]) => list && list.filter((item) => !filters.includes(item.questionId))),
        shareReplay(),
      );
    }
  }

  private setInitialValues(): void {
    if (this.answeredList) {
      this.questionsAnswersForm.controls.forEach((group, i) => {
        if (i < this.answeredList.length) {
          group.setValue({
            [this.QUESTION_FIELD]: this.answeredList[i].questionId,
            [this.ANSWER_FIELD]: this.answeredList[i].answer,
          });
        }
      });
    }
  }

  toggleAllMasks(mask: boolean, index: number) {
    this.toggledEyeIndex = index;
    if (mask) {
      if (this.confirmUnmask) {
        this.showProvidePassword = true;
      } else {
        this.maskCurrent(index);
      }
    } else {
      this.unmaskAll();
    }
  }

  confirmPassword(confirmed: boolean) {
    if (confirmed) {
      this.maskAll();
    }
  }

  private maskCurrent(index: number) {
    const eye = this.fieldEyesComponents.find((item, i) => index === i);
    eye.mask = false;
    eye.inputRef.focus();
  }

  private maskAll() {
    this.fieldEyesComponents.forEach((eye, i) => {
      eye.mask = false;
      if (i === this.toggledEyeIndex) {
        eye.inputRef.focus();
      }
    });
  }

  private unmaskAll() {
    this.fieldEyesComponents.forEach((eye, i) => {
      eye.mask = true;
      if (i === this.toggledEyeIndex) {
        eye.inputRef.focus();
      }
    });
  }

  writeValue(arr: QuestionAnswer[]): void {
    if (arr && arr.length !== 0) {
      this.questionsAnswersForm.patchValue(arr);
    }
  }
  registerOnChange(fn: (value: QuestionAnswer[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: (value: QuestionAnswer[]) => void): void {
    this.onTouch = fn;
  }
}
